import { FC, useEffect, useRef, useState } from 'react';
import { List } from 'src/components';
import { useAudioPlayback } from 'src/context/AudioContext';
import { LearnablesListElement } from '../LearnablesListElement';
import * as S from './styles';

interface Props {
  isLoading?: boolean;
  isRefetching?: boolean;
  list: {
    growthLevel: number;
    targetValue: string;
    sourceValue: string;
    alreadyKnown: boolean;
    difficult: boolean;
    learnableId: number;
    dueForReview?: boolean;
    audioSrc?: string;
  }[];
  isLocked?: boolean;
  difficultDisabled?: boolean;
  refetchUserScenarioDetails?: () => void;
  targetLanguageName?: string;
  sourceLanguageName?: string;
}

export const LearnablesList: FC<Props> = ({
  isLoading,
  isRefetching,
  list,
  isLocked,
  difficultDisabled,
  refetchUserScenarioDetails,
  targetLanguageName,
  sourceLanguageName,
}) => {
  const positionRef = useRef(null);
  const [playingAudioSrc, setPlayingAudioSrc] = useState<string | null>(null);
  const { play, stopAll, playing } = useAudioPlayback(playingAudioSrc);
  const [activeToastId, setActiveToastId] = useState<number | null>(null);

  useEffect(() => {
    if (playingAudioSrc) {
      void play();
    }
  }, [playingAudioSrc, play]);

  useEffect(() => {
    if (!playing) {
      setPlayingAudioSrc(null);
    }
  }, [playing]);

  return isLoading && !isRefetching ? (
    <S.Loader />
  ) : (
    <List data-testid="learnables-list">
      {list.map(
        ({
          learnableId,
          growthLevel,
          targetValue,
          sourceValue,
          alreadyKnown,
          difficult,
          dueForReview,
          audioSrc,
        }) => (
          <LearnablesListElement
            learnableId={learnableId}
            growthLevel={growthLevel}
            targetValue={targetValue}
            sourceValue={sourceValue}
            alreadyKnown={alreadyKnown}
            difficult={difficult}
            dueForReview={dueForReview}
            difficultDisabled={difficultDisabled}
            key={`LearnablesListElement-${learnableId}`}
            isLocked={isLocked}
            refetchUserScenarioDetails={refetchUserScenarioDetails}
            rootElement={positionRef?.current}
            startAudio={audioSrc ? () => setPlayingAudioSrc(audioSrc) : undefined}
            stopAudio={stopAll}
            isPlayingAudio={playing && playingAudioSrc === audioSrc}
            targetLanguageName={targetLanguageName}
            sourceLanguageName={sourceLanguageName}
            activeToastId={activeToastId}
            setActiveToastId={setActiveToastId}
          />
        ),
      )}
    </List>
  );
};
